import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  Select,
  makeStyles
} from '@material-ui/core';
import {
  getAllCCMUsersOfClinicGroup,
  updateCCMAssignedUser
} from '@shared/services/CCMServices';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {Controller, useForm} from 'react-hook-form';
import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import moment from 'moment';
import HelperUtils from '@shared/utils/HelperUtils';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import Loader from '@components/framework/loader/Loader';
import {AppPermissions} from '@shared/constants/Mapping';
import {State} from '@shared/store/reducers/Index';

interface CCMAssigneeAndDueDateDialogProps {
  open: boolean;
  onClose: (response: 'confirm' | 'cancel') => void;
  logId: number | null;
  ccmPatientId: number | null;
  patientName: string;
  defaultDueDate?: string | null;
  defaultAssignTo?: string | null;
}
const useStyles = makeStyles(() => ({
  formStyle: {
    marginBottom: 0,
    paddingBottom: 0
  },
  headerStyle: {
    marginBottom: '0.5rem'
  },
  followUpWrapper: {
    display: 'flex',
    alignItems: 'baseline'
  },
  followUpDueDate: {
    flex: 1,
    marginRight: '1rem'
  }
}));
const CCMAssigneeAndDueDateDialog = (
  props: CCMAssigneeAndDueDateDialogProps
): JSX.Element => {
  const {
    open,
    onClose,
    logId,
    ccmPatientId,
    patientName,
    defaultAssignTo = null,
    defaultDueDate = null
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const classes = useStyles();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [ccmUsers, setCcmUsers] = useState([]);
  const selectedClinicId: any = useSelector(
    (state: any) => state.clinics.selectedClinicId || []
  );
  const {groupId} = useSelector((state: any) => state.auth);
  const [selectedAssignee, setSelectedAssignee] = React.useState(''); // To manage selected assignee
  const {
    control,
    handleSubmit,
    errors,
    setValue,
    watch
    // getValues
  } = useForm({
    mode: 'all',
    resolver: async (values: any) => {
      const error: any = {};

      if (!values?.follow_up_notes_due_date) {
        error.follow_up_notes_due_date = 'CCM.STOP_TIMER.DUE_DATE_IS_REQUIRED';
      } else if (!moment(values?.follow_up_notes_due_date).isValid()) {
        error.follow_up_notes_due_date = 'CCM.STOP_TIMER.INVALID_DATE';
      } else if (
        moment(values?.follow_up_notes_due_date).isBefore(
          moment().startOf('day')
        )
      ) {
        error.follow_up_notes_due_date = 'CCM.STOP_TIMER.INVALID_DUE_DATE';
      }
      if (!values?.follow_up_notes_assignee) {
        error.follow_up_notes_assignee = 'CCM.STOP_TIMER.ASSIGNEE_IS_REQUIRED';
      }

      return {
        values,
        errors: error
      };
    },
    defaultValues: {
      follow_up_notes_due_date: defaultDueDate ?? null,
      follow_up_notes_assignee: defaultAssignTo ?? null
    }
  });

  const isAssigneeSelected = !!watch('follow_up_notes_assignee');
  const isDueDateSelected = !!watch('follow_up_notes_due_date');
  const isButtonDisabled =
    !(isAssigneeSelected && isDueDateSelected) || isLoading;
  // API called for assigning to the ccm user //
  const updateCCMAssigneUser = async (
    _groupId: number,
    clinicId: number,
    assigneeId: number,
    dueDate: string,
    _logId: number | null,
    _ccmPatientId: number | null
  ) => {
    try {
      const updateResponse = await updateCCMAssignedUser(
        _groupId,
        assigneeId,
        _logId,
        _ccmPatientId,
        dueDate,
        {
          clinic_id: clinicId
        }
      );
      if (updateResponse && updateResponse?.message) {
        enqueueSnackbar(updateResponse?.message, SnackBarConfig('s'));
        setIsLoading(false);
        onClose('confirm');
      }
    } catch (error: any) {
      enqueueSnackbar(error && error?.message, SnackBarConfig('e'));
      setIsLoading(false);
    }
  };
  // TODO: Replace this with the redux value once the patient shared redux state issue is fixed
  const {userPermissions} = useSelector((state: State) => state.auth);
  let patientShared = false;
  if (groupId) {
    patientShared =
      userPermissions?.[groupId]?.['group']?.[
        AppPermissions.PATIENT_MENU_PERMISSIONS.PATIENT_SHARED
      ] === true;
  }

  const handleSelectAssignee = (item: any) => {
    setSelectedAssignee(
      HelperUtils.commaSeparatedFullName(item?.first_name, item?.last_name)
    );
  };

  const fetchCCMUsersList = async () => {
    try {
      // eslint-disable-next-line camelcase
      const options: {clinic_id?: number | string} = {};
      if (!patientShared) {
        options['clinic_id'] = selectedClinicId?.[0];
      }
      const response = await getAllCCMUsersOfClinicGroup(groupId, options);
      setCcmUsers(response?.data?.rows);

      // check if the default value exist or not
      if (Array.isArray(response?.data?.rows)) {
        const item = response.data.rows.find(
          (_item: any) => _item.id === defaultAssignTo
        );
        if (item) {
          handleSelectAssignee(item);
        } else {
          setValue('follow_up_notes_assignee', null, {
            shouldValidate: false
          });
        }
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
      throw error;
    }
  };

  useEffect(() => {
    fetchCCMUsersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderFollowUpNotesDueDate = () => {
    return (
      <div className={classes.followUpDueDate}>
        <div className={classes.headerStyle}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_DUE_DATE')}
        </div>
        <FormControl
          className={classes.formStyle}
          fullWidth={true}
          variant="outlined"
          error={errors?.follow_up_notes_due_date}>
          <Controller
            control={control}
            error={errors?.follow_up_notes_due_date}
            name="follow_up_notes_due_date"
            render={({onChange, ...inputProps}) => (
              <DateTimePickerWrapper
                style={{width: '100%'}}
                minDate={new Date()}
                autoComplete="on"
                margin={'dense'}
                type={'string'}
                placeholder={t('CCM.DUE_DATE_PLACEHOLDER')}
                {...inputProps}
                onChange={(date, value) => {
                  setValue('follow_up_notes_due_date', value, {
                    shouldValidate: true
                  });
                  // dispatch(_updateFollowUpDueDateAndAssignee({
                  //   followUpDueDate: value as string
                  // }))
                }}
              />
            )}
            // defaultValue={null}
          />
          {errors?.follow_up_notes_due_date && (
            <FormHelperText>
              {t(errors.follow_up_notes_due_date)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };

  const renderFollowUpNotesAssignee = () => {
    return (
      <div style={{flex: 1}}>
        <div className={classes.headerStyle}>
          {t('CCM.FOLLOW_UP_NOTES_COLUMN.FOLLOW_UP_NOTES_ASSIGNEE')}
        </div>
        <FormControl
          fullWidth={true}
          error={!!errors?.follow_up_notes_assignee}
          variant="outlined"
          className={['select_clinic', classes.formStyle].join(' ')}>
          <Controller
            id="follow_up_notes_assignee"
            control={control}
            name="follow_up_notes_assignee"
            error={!!errors?.follow_up_notes_assignee}
            render={({onBlur}) => (
              <Select
                labelId="demo-multiple-name-label"
                id="demo-multiple-name"
                margin="dense"
                value={selectedAssignee || 'Select Assignee'} // Set selected value in the dropdown
                variant="outlined"
                renderValue={selected => selected as string}
                fullWidth={true}
                onBlur={onBlur}
                name="follow_up_notes_assignee"
                IconComponent={KeyboardArrowDownIcon}>
                <div>
                  {ccmUsers.map((item: any) => (
                    <MenuItem
                      key={item?.id}
                      value={HelperUtils.commaSeparatedFullName(
                        item?.first_name,
                        item?.last_name
                      )}
                      onClick={() => {
                        handleSelectAssignee(item);
                        setValue('follow_up_notes_assignee', item.id, {
                          shouldValidate: true
                        });
                      }}>
                      <ListItemText
                        primary={HelperUtils.commaSeparatedFullName(
                          item?.first_name,
                          item?.last_name
                        )}
                      />
                    </MenuItem>
                  ))}
                </div>
              </Select>
            )}
          />
          {errors?.follow_up_notes_assignee && (
            <FormHelperText>
              {t(errors.follow_up_notes_assignee)}
            </FormHelperText>
          )}
        </FormControl>
      </div>
    );
  };
  const renderFollowUpNotesDueDateAndAssignedUserDetails = () => {
    return (
      <div style={{flex: 1}} className={classes.followUpWrapper}>
        {renderFollowUpNotesDueDate()}
        {renderFollowUpNotesAssignee()}
      </div>
    );
  };
  const onSubmit = async (data: any) => {
    setIsLoading(true);
    updateCCMAssigneUser(
      groupId,
      selectedClinicId?.[0],
      data?.follow_up_notes_assignee,
      data?.follow_up_notes_due_date,
      logId,
      ccmPatientId
    );
  };

  const handleClose = React.useCallback(
    (response: 'confirm' | 'cancel') => {
      if (typeof onClose === 'function') {
        onClose(response);
      }
    },
    [onClose]
  );
  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth={true}
      open={open}
      onClose={handleClose}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}>
      <DialogTitle disableTypography>
        <p>{t('CCM.FOLLOW_UP_NOTES_TITLE')}</p>
      </DialogTitle>
      <DialogContent dividers>
        <div style={{marginBottom: '1rem'}}>
          <span>
            {t('CCM.PATIENT.PATIENT_NAME')}
            {' : '}
          </span>
          <span style={{fontWeight: 600}}>{patientName}</span>
        </div>
        {renderFollowUpNotesDueDateAndAssignedUserDetails()}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose('cancel')}>
          {t('COMMON.CANCEL')}
        </Button>
        <Button
          variant={'contained'}
          onClick={handleSubmit(onSubmit)}
          color="primary"
          disabled={isButtonDisabled}>
          {isLoading ? <Loader style={{color: 'white'}} /> : t('COMMON.UPDATE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default CCMAssigneeAndDueDateDialog;
